import {
    Alert,
    Grid,
    SpeedDial,
    SpeedDialAction,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
} from "@mui/material";
import ConstructionIcon from "@mui/icons-material/Construction";
import DownloadingIcon from "@mui/icons-material/Downloading";
import FilterAltOffIcon from "@mui/icons-material/FilterAltOff";
import QueryStatsIcon from "@mui/icons-material/QueryStats";
import CheckIcon from "@mui/icons-material/Check";
import ClearIcon from "@mui/icons-material/Clear";

import { useAppDispatch, useAppSelector } from "app/hooks";
import { playbackActions } from "./store/slice";
import { playbackSessionSelectors } from "./store/selectors.session";
import { playbackDebugSelectors } from "./store/selectors.debug";
import { playbackSelectionSelectors } from "./store/selectors.selection";
import { JSX } from "react";

export function PlaybackDebugControls() {
    const dispatch = useAppDispatch();

    const debugShowStatistics = useAppSelector(
        playbackDebugSelectors.selectShowStatistics,
    );

    const readyForPlaybackStatistics = useAppSelector(
        playbackSessionSelectors.selectReadyForPlaybackStatistics,
    );

    const userRequestedPlayMode = useAppSelector(
        playbackSessionSelectors.selectUserRequestedPlayMode,
    );
    const playbackPlaying = useAppSelector(
        playbackSessionSelectors.selectPlaybackPlaying,
    );

    const allRecordings = useAppSelector(
        playbackSessionSelectors.selectAllRecordings,
    );

    const deviceIds = useAppSelector(
        playbackSelectionSelectors.selectDeviceIds,
    );
    const sessionId = useAppSelector(playbackSessionSelectors.selectSessionId);

    const actions = [
        {
            icon: <QueryStatsIcon />,
            name: "Show statistics",
            onClick: () =>
                dispatch(playbackActions.toggleDebugShowStatistics()),
        },
        {
            icon: <DownloadingIcon />,
            name: "Show buffering",
            onClick: () =>
                dispatch(playbackActions.toggleDebugShowBufferingRecordings()),
        },
        {
            icon: <FilterAltOffIcon />,
            name: "Show all",
            onClick: () =>
                dispatch(
                    playbackActions.toggleDebugShowRecordingsOutsidePlaybackAndBufferingWindow(),
                ),
        },
    ];

    const tableRowsGeneric: {
        key: string;
        content: JSX.Element | string | number | undefined;
    }[] = [
        { key: "Session ID", content: sessionId },
        {
            key: "User selected play mode",
            content: userRequestedPlayMode,
        },
        {
            key: "Playing",
            content: playbackPlaying ? <CheckIcon /> : <ClearIcon />,
        },
        { key: "Total recordings", content: allRecordings.length },
        { key: "Total devices", content: deviceIds.length },
    ];

    const tableRowsBuffer: {
        key: string;
        content: JSX.Element | string | number | undefined;
    }[] = [
        {
            key: "Total recordings",
            content: readyForPlaybackStatistics.totalRecordings,
        },
        {
            key: "Total ready for playback",
            content: readyForPlaybackStatistics.totalReadyForPlayback,
        },
        {
            key: "Failed to load",
            content: readyForPlaybackStatistics.totalFailedToLoad,
        },
        {
            key: "Waiting",
            content: readyForPlaybackStatistics.totalWaiting,
        },
        {
            key: "Ready for playback",
            content: readyForPlaybackStatistics.ready ? (
                <CheckIcon />
            ) : (
                <ClearIcon />
            ),
        },
    ];

    return (
        <>
            {debugShowStatistics && (
                <Alert
                    icon={false}
                    severity="info"
                    sx={{
                        marginBottom: 2,
                        ".MuiAlert-message": {
                            width: "100%",
                        },
                    }}
                >
                    <Grid
                        container
                        spacing={2}
                    >
                        <Grid
                            size={{
                                xs: 6,
                            }}
                        >
                            <Table
                                size="small"
                                sx={{ width: "100%" }}
                            >
                                <TableHead>
                                    <TableRow>
                                        <TableCell colSpan={2}>
                                            Generic statistics
                                        </TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {tableRowsGeneric &&
                                        tableRowsGeneric.map((row) => (
                                            <TableRow key={row.key}>
                                                <TableCell
                                                    component="th"
                                                    scope="row"
                                                >
                                                    {row.key}
                                                </TableCell>
                                                <TableCell>
                                                    {row.content}
                                                </TableCell>
                                            </TableRow>
                                        ))}
                                </TableBody>
                            </Table>
                        </Grid>
                        <Grid
                            size={{
                                xs: 6,
                            }}
                        >
                            <Table
                                size="small"
                                sx={{ width: "100%" }}
                            >
                                <TableHead>
                                    <TableRow>
                                        <TableCell colSpan={2}>
                                            Buffer & playback window statistics
                                        </TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {tableRowsBuffer &&
                                        tableRowsBuffer.map((row) => (
                                            <TableRow key={row.key}>
                                                <TableCell
                                                    component="th"
                                                    scope="row"
                                                >
                                                    {row.key}
                                                </TableCell>
                                                <TableCell>
                                                    {row.content}
                                                </TableCell>
                                            </TableRow>
                                        ))}
                                </TableBody>
                            </Table>
                        </Grid>
                    </Grid>
                </Alert>
            )}

            <SpeedDial
                ariaLabel={"Debug controls"}
                sx={{
                    position: "absolute",
                    top: 80,
                    right: 16,
                }}
                icon={<ConstructionIcon />}
                direction="left"
            >
                {actions.map((action) => (
                    <SpeedDialAction
                        key={action.name}
                        icon={action.icon}
                        tooltipTitle={action.name}
                        onClick={action.onClick}
                    />
                ))}
            </SpeedDial>
        </>
    );
}
